import React, { useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import SwiperClass from 'swiper';

import { styled } from '@mui/material';

import { CircleCheckmarkIcon } from '@mewa/ui';

import Image from '../../../components/image';
import { ProductFallbackImage } from '../../../components/product-fallback-image';
import { jpgPath } from '../../../helpers/image-factory';
import { Props } from '../types';

export const ThumbSwiper = ({
  variants,
  setThumbSwiper,
  thumbCount,
  loopSlides,
  useMbsFallbackIcon,
  onSlideChange,
  activeIndex,
}: {
  variants: Props['pimProduct']['variants'];
  setThumbSwiper?: React.Dispatch<React.SetStateAction<SwiperClass | null>>;
  thumbCount: number;
  loopSlides: boolean;
  useMbsFallbackIcon: boolean;
  onSlideChange: (i: number) => void;
  activeIndex: number;
}) => {
  const isInitialized = useRef<boolean>(false);

  const handleSlideChange = (swiper: SwiperClass) => {
    if (isInitialized.current) {
      onSlideChange(swiper.realIndex);
    }
  };

  return (
    <Swiper
      slidesPerView={thumbCount}
      centeredSlides={true}
      spaceBetween={36}
      loop={loopSlides}
      loopedSlides={thumbCount}
      slideToClickedSlide={true}
      onSwiper={setThumbSwiper}
      allowTouchMove={false}
      roundLengths
      onSlideChange={handleSlideChange}
      onAfterInit={() => {
        isInitialized.current = true;
      }}
    >
      {variants.map((variant, index) => (
        <SwiperSlide key={variant.name}>
          <ThumbSlideContent
            key={variant.name}
            variant={variant}
            isActive={index === activeIndex}
            useMbsFallbackIcon={useMbsFallbackIcon}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const ThumbSlideContent = React.memo(
  ({
    variant,
    isActive,
    useMbsFallbackIcon,
  }: {
    variant: Props['pimProduct']['variants'][0];
    isActive: boolean;
    useMbsFallbackIcon: boolean;
  }) => {
    const imgData = slideImg(variant.assetId ?? '', 150);

    return (
      <ThumbSlideImageWrapper>
        {/*
              We use a regular img tag here instead of a gatsby image because we
              don't need to load multiple sizes for different viewports and we
              want to load all images as soon as possible even if they are not
              in the viewport yet.
          */}
        {isActive && (
          <ActiveThumbIconWrapper>
            <CircleCheckmarkIcon color="var(--color-light-red)" />
          </ActiveThumbIconWrapper>
        )}
        {variant.assetId ? (
          <Image images={[imgData]} alt={variant.colorName ?? ''} />
        ) : (
          <DarkProductFallbackImage useMbsFallbackIcon={useMbsFallbackIcon} />
        )}
      </ThumbSlideImageWrapper>
    );
  },
);

const DarkProductFallbackImage = styled(ProductFallbackImage)({
  fill: 'var(--color-medium-grey)',
});

const ThumbSlideImageWrapper = styled('div')({
  position: 'relative',

  img: {
    width: '100%',
    height: 'auto',
  },
});

const ActiveThumbIconWrapper = styled('div')({
  position: 'absolute',
  left: '50%',
  transform: 'translate(-50%, 30%)',
  bottom: '0px',
  zIndex: 1,
  width: '36px',
  height: '36px',
});

const slideImg = (assetId: string, size: number, media = '') => ({
  src: jpgPath(assetId, size, size),
  media: media,
  width: size,
  height: size,
});
